import React, { useState, useContext } from "react";
import { useLocation, useHistory, matchPath } from "react-router-dom";
import _ from "lodash";
import "./style.css";

const basename = '/app'

const Context = React.createContext(null);

export const useSidebarContext = () => {
  return useContext(Context);
};

export const SidebarProvider = ({ children }) => {
  const [open, setOpen] = useState(true);
  return (
    <Context.Provider value={{ open, setOpen }}>{children}</Context.Provider>
  );
};

export const SidebarToggler = () => {
  const { open, setOpen } = useSidebarContext();
  // "Collapse", not "collapse to icons": what collapsing leaves behind depends
  // on the viewport — an icon rail at md and up, nothing at all below it, where
  // the rail is already as narrow as the sidebar gets.
  const hint = open ? "Collapse the sidebar" : "Expand the sidebar";

  // Collapsing is a class on #sidebar, not a jQuery show()/hide(). Those wrote
  // an inline `display` that outranked both the .collapse classes the sidebar
  // carries and the small-screen rules in style.css, and hiding it outright
  // took the icons with it — there was nothing left to click but this toggler.
  return (
    <a
      href="#"
      aria-expanded={open}
      aria-label={hint}
      title={hint}
      onClick={(e) => {
        e.preventDefault();
        setOpen(!open);
        return false;
      }}
    >
      <i className="fa fa-navicon mx-2"></i>
    </a>
  );
};

export const Sidebar = function ({ menu, children }) {
  const history = useHistory();
  const sidebar = useSidebarContext();

  // The provider is optional — Navbar already guards on the context being
  // there — and without one there is nothing that can collapse the sidebar.
  const iconsOnly = sidebar ? !sidebar.open : false;

  return (
    <div className="container-fluid">
      <div className="row d-flex d-md-block flex-nowrap wrapper">
        <div
          className={
            "col-md-3 float-left col-1 pl-0 pr-0 collapse width show" +
            (iconsOnly ? " icons-only" : "")
          }
          id="sidebar"
        >
          <div className="list-group border-0 card text-center text-md-left">
            {_.map(menu, (item, index) => (
              <SidebarItem
                key={"sidebar_" + index}
                parent={"sidebar"}
                index={index}
                {...item}
                history={history}
              />
            ))}
          </div>
        </div>
      </div>
      {/* sidebar-content: below md the rail is position:fixed, so it is out of
          flow and the content starts underneath it. style.css reserves its
          width here — the two sit side by side at every size. rail-hidden says
          the rail has slid off-canvas, so that reservation is given back. */}
      <div
        className={
          "row no-gutters sidebar-content" + (iconsOnly ? " rail-hidden" : "")
        }
      >
        <div className="col">
          {children}
        </div>
      </div>
    </div>
  );
};

export const SidebarItem = ({
  parent,
  index,
  index: parentIndex,
  icon,
  label,
  to,
  submenu,
  history,
}) => { 
  const menuId = `menu${index}`;
  const iconRes = icon ? <i className={"fa fa-" + icon}></i> : null;
  const leaf = !submenu || submenu.length === 0;

  let current = false;

  const { pathname } = useLocation();

  if (matchPath(pathname, { path: to, exact: leaf })) {
    current = true;
  }

  // title, on both branches below: collapsed to icons the label is the only
  // thing naming the destination, so it has to survive as a tooltip and as the
  // accessible name the hidden span stops supplying.
  return leaf ? (
    <a
      href={`${basename}${to}`}
      className={"list-group-item " + (current ? "" : "collapsed")}
      data-parent={"#" + parent}
      title={label}
      onClick={(e) => {
        e.preventDefault();
        //const trigger = document.getElementById("trigger" + parent);
        //trigger && trigger.click();
        history.push(to);
        return false;
      }}
    >
      {iconRes}{" "}
      <span
        className={
          "sidebar-label " +
          (parent === "sidebar" ? "d-none d-md-inline" : "inline")
        }
      >
        {label}
      </span>
    </a>
  ) : (
    <>
      <a
        id={"trigger" + menuId}
        href={"#" + menuId}
        className={"list-group-item d-inline-block collapsed"}
        data-toggle="collapse"
        aria-expanded="false"
        title={label}
        /*onClick={(e) => {
          e.preventDefault();
          history.push(to);
          return false;
        }}*/
      >
        {iconRes}{" "}
        <span className="sidebar-label d-none d-md-inline">{label}</span>{" "}
      </a>
      <div className={"collapse"} id={menuId} data-parent={"#" + parent}>
        {_.map(submenu, (item, index) => (
          <SidebarItem
            key={`${menuId}_${index}`}
            parent={menuId}
            index={parentIndex + "" + index}
            {...item}
            history={history}
          />
        ))}
      </div>
    </>
  );
};
